import { forwardRef } from 'react';

// Direct file import (not the icons barrel) — the barrel re-exports every
// lucide shim, which would pull `lucide-react` into web bundles that don't
// ship it (flashka-app's 'use dom' WebViews).
import { CircleX } from '../../icons/custom/CircleX';
import { Platform } from '../../platform';
import { Pressable, type PressEvent } from '../../primitives/Pressable';
import { Text } from '../../primitives/Text';
import { useTheme } from '../../theme';
import { cn } from '../../utils/cn';
import { subTestID } from '../../utils/testId';
import { ICON_SIZE_FOR, resolveChipIconColor } from './Chip.icons';
import type { ChipProps } from './Chip.types';
import { chipTextVariants, chipVariants } from './Chip.variants';

const IS_WEB = Platform.OS === 'web';

// Detached ref shape — see Pressable.tsx / Button.tsx for the rationale
// (pnpm hoists multiple @types/react copies; React's `Ref<T>` becomes
// nominally distinct across consumer apps even when versions match).
type AnyRef<T> = ((node: T | null) => void) | { current: T | null } | null;

/**
 * Native resting drop shadow — the cross-platform mirror of `web:shadow-chip`
 * (Figma "Drop small": `0 2.5px 10px 0 #0000000A`). Applied as an RN style
 * object via `nativeProps.style` rather than a NativeWind `shadow-*` class,
 * matching Button's approach (NativeWind v4's `boxShadow` handling on RN 0.83
 * is unreliable). 4% black is barely perceptible on mobile, so the web-only
 * press-removal of the shadow isn't mirrored here.
 */
const NATIVE_CHIP_SHADOW = {
  shadowColor: '#000000',
  shadowOffset: { width: 0, height: 2.5 },
  shadowOpacity: 0.04,
  shadowRadius: 10,
  elevation: 1,
} as const;

/**
 * Cross-platform filter Chip. Renders identically on web and React Native.
 *
 * The `tint` prop IS the selection axis: `'neutral'` is the unselected gray
 * chip (with full Hover / Pressed / Focused / Disabled states); the colored
 * tints (`'new'` / `'review'` / `'memorised'` / `'toReview'`) are the Figma
 * "Selected" state with the matching progress color.
 *
 * @example
 * ```tsx
 * import { Chip } from '@flashka/shared/ui';
 * import { GraduationCap } from '@flashka/shared/ui/icons';
 *
 * // Unselected filter
 * <Chip tint="neutral" onPress={toggle}>All decks</Chip>
 *
 * // Selected, with leading icon
 * <Chip tint="new" size="l" leftIcon={GraduationCap} onPress={toggle}>New</Chip>
 *
 * // Removable tag
 * <Chip tint="review" removable onRemove={remove} removeAccessibilityLabel="Remove Biology">
 *   Biology
 * </Chip>
 * ```
 */
function ChipInner(
  {
    children,
    onPress,
    leftIcon: LeftIcon,
    iconColor: iconColorOverride,
    contentTint,
    removable = false,
    onRemove,
    disabled = false,
    haptic = true,
    tint,
    size,
    className,
    textClassName,
    accessibilityLabel,
    removeAccessibilityLabel,
    testID,
    webProps,
    nativeProps,
    onHoverIn,
    onHoverOut,
    // Slot-style consumers (Radix `Trigger asChild`, etc.) clone the Chip and
    // attach extra props / a ref at the top level — funnel them through to
    // the Pressable, which composes them with its own handlers.
    ...injected
  }: ChipProps & Record<string, unknown>,
  ref: AnyRef<HTMLElement>,
) {
  const { isDark } = useTheme();
  const resolvedTint = tint ?? 'neutral';
  const resolvedSize = size ?? 'm';
  // The content (icon + label) can take a tint independent of the body
  // (`contentTint`) so an unselected — i.e. `neutral`-bodied — filter chip
  // still shows its filter color on both the icon and the label. Falls back to
  // the body tint.
  const resolvedContentTint = contentTint ?? resolvedTint;

  const textClass = cn(
    chipTextVariants({ tint: resolvedContentTint, size: resolvedSize, disabled }),
    textClassName,
  );
  // Also paint the tint text COLOUR on the Pressable container so icons
  // (rendered as siblings of the inner <Text>) pick it up via CSS
  // `currentColor` inheritance on web — same pattern as Button.
  const containerClass = cn(
    chipVariants({ tint: resolvedTint, size: resolvedSize, removable, disabled }),
    textClass,
    className,
  );

  // On web, pass `currentColor` so the SVG inherits its colour from the
  // Pressable's CSS `text-*` class — which is the content tint (see
  // `textClass`), so the icon matches the label automatically, incl. Tailwind
  // `dark:` scoping. On native, `currentColor` is a CSS construct
  // react-native-svg doesn't honour, so resolve the content tint's hex.
  const iconColor =
    iconColorOverride ??
    (IS_WEB ? 'currentColor' : resolveChipIconColor(resolvedContentTint, isDark));
  const iconSize = ICON_SIZE_FOR[resolvedSize];

  // Merge the native resting shadow into the caller's nativeProps. RN's
  // `style` accepts an array (later entries win), so a caller's custom style
  // still overrides where keys overlap. Web swallows nativeProps entirely.
  const mergedNativeProps = {
    ...(nativeProps ?? {}),
    style: [
      NATIVE_CHIP_SHADOW,
      (nativeProps as { style?: unknown } | undefined)?.style,
    ].filter(Boolean),
  };

  // Wrap a text-like child (string / number / array of those) in Text so the
  // tint typography classes apply. Pass JSX through untouched.
  const label = isTextLike(children) ? (
    <Text className={textClass}>{children}</Text>
  ) : (
    children
  );

  // Remove control — a nested pressable rendered as a div (role=button) so
  // it's valid to nest inside the outer chip <button> without a <button>-in-
  // <button> parse error. Stops propagation so pressing it does NOT also fire
  // the chip's `onPress`. Inherits `opacity-50` from the chip when disabled.
  const handleRemove = (event: PressEvent) => {
    event.stopPropagation?.();
    if (!disabled) onRemove?.(event);
  };

  const removeControl = removable ? (
    <Pressable
      as="div"
      disabled={disabled}
      onPress={handleRemove}
      accessibilityRole="button"
      accessibilityLabel={removeAccessibilityLabel ?? 'Remove'}
      testID={subTestID(testID, 'remove')}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full',
        !disabled && 'web:transition-opacity web:duration-150 web:hover:opacity-70',
      )}>
      <CircleX color={iconColor} size={iconSize} />
    </Pressable>
  ) : null;

  return (
    <Pressable
      {...injected}
      ref={ref}
      className={containerClass}
      disabled={disabled}
      haptic={haptic}
      onPress={onPress}
      onHoverIn={onHoverIn}
      onHoverOut={onHoverOut}
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="button"
      testID={testID}
      webProps={webProps}
      nativeProps={mergedNativeProps}>
      {LeftIcon ? <LeftIcon color={iconColor} size={iconSize} /> : null}
      {label}
      {removeControl}
    </Pressable>
  );
}

/**
 * Public Chip. `forwardRef` so `asChild` consumers can hand the underlying
 * DOM element back for positioning / focus management. Cast through
 * `unknown` to detach from React's `Ref<T>` generic — see `AnyRef` and the
 * analogous pattern in `Pressable.tsx` / `Button.tsx`.
 */
export const Chip = forwardRef(
  ChipInner as never,
) as unknown as React.ForwardRefExoticComponent<
  ChipProps & { ref?: AnyRef<HTMLElement> }
>;
(Chip as { displayName?: string }).displayName = 'Chip';

function isTextLike(c: unknown): boolean {
  if (c == null || typeof c === 'boolean') return false;
  if (typeof c === 'string' || typeof c === 'number') return true;
  if (Array.isArray(c)) return c.every(isTextLike);
  return false;
}
